'use client';

import { cn, useIsBreakpoint } from '@bridebook/aura';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTopBar,
  type DrawerProps,
  type DrawerTopBarProps,
} from '../../drawer/drawer';
import { DrawerOrModalContext, useDrawerOrModalContext } from './drawer-or-modal-context';
import {
  RoundedModal,
  type RoundedModalContainerHeight,
  type RoundedModalContainerWidth,
} from '../rounded-modal/rounded-modal';
import { RoundedModalContent } from '../rounded-modal/rounded-modal-content';
import { RoundedModalFooter } from '../rounded-modal/rounded-modal-footer';
import { RoundedModalHeader, RoundedModalTitle } from '../rounded-modal/rounded-modal-header';
import { type ComponentPropsWithoutRef, type ReactNode, useMemo } from 'react';

// ──────────────────────────────────────────────
// Root component types
// ──────────────────────────────────────────────

type DrawerOrModalDrawerProps = Pick<DrawerProps, 'classNames'> & {
  /** Configuration for the DrawerTopBar */
  topBar?: Pick<DrawerTopBarProps, 'layout' | 'classNames'>;
};

type DrawerOrModalModalProps = {
  /** Width variant for the modal container. Default: 'standard' */
  width?: RoundedModalContainerWidth;
  /** Height variant for the modal container. Default: 'auto-height' */
  height?: RoundedModalContainerHeight;
  /** Callback when modal open auto-focus fires (from Radix Dialog) */
  onOpenAutoFocus?: ComponentPropsWithoutRef<typeof RoundedModal>['onOpenAutoFocus'];
  /** Custom classNames for the Modal overlay */
  classNames?: ComponentPropsWithoutRef<typeof RoundedModal>['classNames'];
};

type DrawerOrModalProps = {
  children?: ReactNode;
  /** Whether the modal/drawer is open */
  isOpen: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Force-close function bypassing onOpenChange */
  forceClose?: () => void;
  /**
   * Override the breakpoint-based layout detection.
   * `true` = render as modal, `false` = render as drawer.
   * When omitted, uses `useIsBreakpoint('--breakpoint-md')` (768px).
   */
  isAboveBreakpoint?: boolean;
  /** Whether to show the close button. Default: true */
  showCloseButton?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Props forwarded only to the Drawer rendering */
  drawerProps?: DrawerOrModalDrawerProps;
  /** Props forwarded only to the RoundedModal rendering */
  modalProps?: DrawerOrModalModalProps;
};

/**
 * Rounded modal on desktop and bottom drawer on mobile (default switch at `--breakpoint-md`, 768px).
 * Intended for **simple** flows where the same layout fits both modes. For complex UIs that need
 * different structure per breakpoint, use `Drawer` and `RoundedModal` separately.
 */
export const DrawerOrModal = ({
  children,
  isOpen,
  onOpenChange,
  forceClose,
  isAboveBreakpoint: isAboveBreakpointProp,
  showCloseButton = true,
  className,
  drawerProps,
  modalProps,
}: DrawerOrModalProps) => {
  const isAboveBreakpointDefault = useIsBreakpoint('--breakpoint-md');
  const isAboveBreakpoint = isAboveBreakpointProp ?? isAboveBreakpointDefault;

  const contextValue = useMemo(
    () => ({ isAboveBreakpoint, showCloseButton, modalWidth: modalProps?.width ?? 'standard' }),
    [isAboveBreakpoint, showCloseButton, modalProps?.width],
  );

  return (
    <DrawerOrModalContext.Provider value={contextValue}>
      {isAboveBreakpoint ? (
        <RoundedModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          forceClose={forceClose}
          width={modalProps?.width ?? 'standard'}
          height={modalProps?.height ?? 'auto-height'}
          onOpenAutoFocus={modalProps?.onOpenAutoFocus}
          className={className}
          classNames={modalProps?.classNames}>
          {children}
        </RoundedModal>
      ) : (
        <Drawer
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          forceClose={forceClose}
          className={className}
          classNames={drawerProps?.classNames}>
          <DrawerTopBar
            withCloseButton={showCloseButton}
            layout={drawerProps?.topBar?.layout}
            classNames={drawerProps?.topBar?.classNames}
          />
          <DrawerBody>{children}</DrawerBody>
        </Drawer>
      )}
    </DrawerOrModalContext.Provider>
  );
};

// ──────────────────────────────────────────────
// Header
// ──────────────────────────────────────────────

type DrawerOrModalHeaderDrawerProps = {
  /** Size of the DrawerTitle. Default: 'lg' */
  titleSize?: ComponentPropsWithoutRef<typeof DrawerTitle>['size'];
};

type DrawerOrModalHeaderModalProps = Pick<
  ComponentPropsWithoutRef<typeof RoundedModalHeader>,
  'variant' | 'closeButtonProps' | 'onBackButtonClick' | 'backButtonProps'
> & {
  /** Title variant for RoundedModalTitle */
  titleVariant?: ComponentPropsWithoutRef<typeof RoundedModalTitle>['variant'];
};

type DrawerOrModalHeaderProps = {
  children?: ReactNode;
  className?: string;
  /** Props forwarded only when rendering as Drawer */
  drawerProps?: DrawerOrModalHeaderDrawerProps;
  /** Props forwarded only when rendering as Modal */
  modalProps?: DrawerOrModalHeaderModalProps;
};

export const DrawerOrModalHeader = ({
  children,
  className,
  drawerProps,
  modalProps,
}: DrawerOrModalHeaderProps) => {
  const { isAboveBreakpoint, showCloseButton } = useDrawerOrModalContext();

  if (isAboveBreakpoint) {
    return (
      <RoundedModalHeader
        showCloseButton={showCloseButton}
        variant={modalProps?.variant}
        closeButtonProps={modalProps?.closeButtonProps}
        onBackButtonClick={modalProps?.onBackButtonClick}
        backButtonProps={modalProps?.backButtonProps}
        className={className}>
        {children && (
          <RoundedModalTitle variant={modalProps?.titleVariant}>{children}</RoundedModalTitle>
        )}
      </RoundedModalHeader>
    );
  }

  return (
    <DrawerHeader className={className}>
      <DrawerTitle size={drawerProps?.titleSize ?? 'lg'}>{children}</DrawerTitle>
    </DrawerHeader>
  );
};

// ──────────────────────────────────────────────
// Content
// ──────────────────────────────────────────────

type DrawerOrModalContentProps = {
  children?: ReactNode;
  className?: string;
};

export const DrawerOrModalContent = ({ children, className }: DrawerOrModalContentProps) => {
  const { isAboveBreakpoint } = useDrawerOrModalContext();

  if (isAboveBreakpoint) {
    return <RoundedModalContent className={className}>{children}</RoundedModalContent>;
  }

  return <DrawerContent className={className}>{children}</DrawerContent>;
};

// ──────────────────────────────────────────────
// Footer
// ──────────────────────────────────────────────

type DrawerOrModalFooterProps = {
  children?: ReactNode;
  className?: string;
};

export const DrawerOrModalFooter = ({ children, className }: DrawerOrModalFooterProps) => {
  const { isAboveBreakpoint, modalWidth } = useDrawerOrModalContext();

  const extraRoundedModalFooterClassName = useMemo(() => {
    switch (modalWidth) {
      case 'standard':
      case 'lg':
        return 'flex-row-reverse';
      default:
        return '';
    }
  }, [modalWidth]);

  if (isAboveBreakpoint) {
    return (
      <RoundedModalFooter className={cn(extraRoundedModalFooterClassName, className)}>
        {children}
      </RoundedModalFooter>
    );
  }

  return <DrawerFooter className={cn(className)}>{children}</DrawerFooter>;
};
